import { useMemo } from "react";

import {
  filterWriterCreativesByAllowedChannels,
  getWriterCreativesFromStrategyData,
} from "components/modals/add-creatives-with-writer-modal/add-creatives-with-writer-modal.helper";
import type { WriterCreative } from "components/modals/add-creatives-with-writer-modal/add-creatives-with-writer-modal.types";

import { useAllowedCreativeChannels } from "hooks/api/use-allowed-creative-channels";
import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useTouchpoints } from "hooks/api/use-touchpoints";

interface UseCampaignWriterCreativesResult {
  creatives: WriterCreative[];
  isLoading: boolean;
  isError: boolean;
}

/**
 * Creative items from the campaign's strategy data that are not yet associated with a
 * touchpoint, and whose channel is still selected on the client's A2A/A2E program strategy.
 * A strategy creative item's `id` is the same value a touchpoint created from it stores as
 * `templateCreativeId`, so "unused" is a set difference against the campaign's existing
 * touchpoints, with no per-touchpoint detail fetch needed.
 */
export const useCampaignWriterCreatives = (): UseCampaignWriterCreativesResult => {
  const campaign = useCurrentCampaign();
  const { allowedChannels, isLoadingAllowedChannels, isErrorAllowedChannels } = useAllowedCreativeChannels(
    campaign.programType,
  );

  const allCreatives = useMemo(
    () =>
      filterWriterCreativesByAllowedChannels(
        getWriterCreativesFromStrategyData(campaign.strategyData, campaign.schemaVersion),
        allowedChannels,
      ),
    [campaign.strategyData, campaign.schemaVersion, allowedChannels],
  );

  const { data: touchpointsData, isLoading, isError } = useTouchpoints({});

  const usedCreativeIds = new Set(
    (touchpointsData?.items ?? [])
      .map((touchpoint) => touchpoint.templateCreativeId)
      .filter((templateCreativeId): templateCreativeId is string => Boolean(templateCreativeId)),
  );

  const isCreativesLoading = isLoading || isLoadingAllowedChannels;
  const isCreativesError = isError || isErrorAllowedChannels;

  // Without the touchpoint list the "already used" set is unknown, so no creative can be
  // offered as available: the backend rejects the whole batch if one is already in use.
  return {
    creatives:
      isCreativesLoading || isCreativesError
        ? []
        : allCreatives.filter((creative) => !usedCreativeIds.has(creative.id)),
    isLoading: isCreativesLoading,
    isError: isCreativesError,
  };
};
